// NEW_FILE_JUSTIFIED: Settings component for connecting personal Telegram account
import React, { useState, useCallback } from 'react';
import { useQuery, useAction } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: _generated is the only allowed convex import from src/components
import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { MessageSquare, CheckCircle } from 'lucide-react';
import type { TConnectStep } from '@/@types/telegram';
import {
  TelegramPhoneForm,
  TelegramOtpForm,
  TelegramConnectedView,
  TelegramConnectingSpinner,
} from './TelegramConnectSteps';

export function TelegramPersonalConnect() {
  const session = useQuery(api.telegram.personal.queries.getMySession, {});

  const initiateConnect = useAction(api.telegram.personal.sessions.initiateConnect);
  const verifyOtp = useAction(api.telegram.personal.sessions.verifyOtp);
  const disconnect = useAction(api.telegram.personal.sessions.disconnect);

  const [step, setStep] = useState<TConnectStep>('disconnected');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleConnect = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setErrorMessage(null);
      const form = e.currentTarget;
      const phoneNumber = (form.elements.namedItem('phone') as HTMLInputElement).value.trim(); // AS_JUSTIFIED: form.elements.namedItem returns Element|null; input is always HTMLInputElement by name
      if (!phoneNumber) return;

      setStep('connecting');
      try {
        const result = await initiateConnect({ phoneNumber });
        setStep(result.requires2FA ? 'awaiting_2fa' : 'awaiting_otp');
      } catch (err) {
        setErrorMessage(err instanceof Error ? err.message : 'Failed to send OTP');
        setStep('disconnected');
      }
    },
    [initiateConnect]
  );

  const handleVerify = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setErrorMessage(null);
      const form = e.currentTarget;
      const otp = (form.elements.namedItem('otp') as HTMLInputElement).value.trim(); // AS_JUSTIFIED: form.elements.namedItem returns Element|null; input is always HTMLInputElement by name
      const passwordEl = form.elements.namedItem('password') as HTMLInputElement | null; // AS_JUSTIFIED: typed cast for optional named password input; always HTMLInputElement when present
      const password = passwordEl?.value.trim();

      setStep('connecting');
      try {
        await verifyOtp({ otp, password: password ?? undefined });
        setStep('connected');
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Verification failed';
        if (message.includes('2FA_REQUIRED')) {
          setStep('awaiting_2fa');
          setErrorMessage('Cloud password required');
        } else {
          setErrorMessage(message);
          setStep('awaiting_otp');
        }
      }
    },
    [verifyOtp]
  );

  const handleDisconnect = useCallback(async () => {
    setErrorMessage(null);
    try {
      await disconnect({});
      setStep('disconnected');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Disconnect failed');
    }
  }, [disconnect]);

  const handleCancelOtp = useCallback(() => {
    setStep('disconnected');
    setErrorMessage(null);
  }, []);

  // Derive actual displayed state from Convex session + local step
  const isConnected = session?.status === 'active' || step === 'connected';
  const isLoading = step === 'connecting' || session === undefined;

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
          <MessageSquare size={18} className="text-muted-foreground" aria-hidden="true" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-foreground">Personal Telegram</h3>
          <p className="text-xs text-muted-foreground">Receive your Telegram DMs in Messages</p>
        </div>

        {/* Status indicator */}
        {isConnected && (
          <div className="ml-auto flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
            <CheckCircle size={13} aria-hidden="true" />
            <span>Connected</span>
          </div>
        )}
      </div>

      <Separator />

      {/* Error — always rendered so live region is registered before announcement */}
      <p
        role="alert"
        className={cn(
          'rounded-lg px-3 py-2 text-xs text-destructive',
          errorMessage ? 'bg-destructive/10' : 'hidden'
        )}
      >
        {errorMessage ?? ''}
      </p>

      {/* State: Connected */}
      {isConnected && (
        <TelegramConnectedView
          phoneNumber={session?.phoneNumber ?? 'your account'}
          onDisconnect={handleDisconnect}
        />
      )}

      {/* State: Enter phone number */}
      {!isConnected && step === 'disconnected' && (
        <TelegramPhoneForm onSubmit={handleConnect} isLoading={isLoading} />
      )}

      {/* State: Loading / Connecting */}
      {step === 'connecting' && <TelegramConnectingSpinner />}

      {/* State: Enter OTP (and optional 2FA password) */}
      {(step === 'awaiting_otp' || step === 'awaiting_2fa') && (
        <TelegramOtpForm
          step={step}
          onSubmit={handleVerify}
          onCancel={handleCancelOtp}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}
